import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect, useRef, useCallback } from 'react';
import { BASE_API_URL } from '../helpers/Config';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { TriggerBalanceUpdate } from '../helpers/TriggerBalanceUpdate';

const StakingCommission = ({ theme = 'dark' }) => {
    const isDarkMode = theme === 'dark';
    const MIN_CLAIM_AMOUNT = 1;

    // Separated states
    const [unclaimedAmount, setUnclaimedAmount] = useState(0);
    const [records, setRecords] = useState([]);

    const [stats, setStats] = useState({
        unclaimed: 0,
        claimed: 0,
        earned: 0,
        record: 0
    });
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [statusFilter, setStatusFilter] = useState('all');
    const [lastId, setLastId] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [showRules, setShowRules] = useState(false);

    // New state for filter counts
    const [filterCounts, setFilterCounts] = useState({
        all: 0,
        success: 0,
        disqualified: 0
    });

    // Commission rates data from API
    const [commissionRates, setCommissionRates] = useState([]);

    const navigate = useNavigate();
    const observer = useRef();
    const lastRecordRef = useCallback((node) => {
        if (loadingMore) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                loadMoreRecords();
            }
        });
        if (node) observer.current.observe(node);
    }, [loadingMore, hasMore]);

    // Theme colors
    const themeColors = {
        dark: {
            background: '#121826',
            surface: '#1E293B',
            surfaceLight: '#2D3748',
            primary: '#8B5CF6',
            primaryHover: '#7C3AED',
            text: '#E5E7EB',
            textSecondary: '#9CA3AF',
            border: '#374151',
            icon: '#9CA3AF',
            shadow: '0 4px 6px -1px rgba(0, 0, 0, 0.3)',
            success: '#10B981',
            warning: '#F59E0B',
            info: '#3B82F6',
            error: '#EF4444',
            gradientPrimary: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
            gradientSuccess: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
            buttonShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
        },
        light: {
            background: '#F9FAFB',
            surface: '#FFFFFF',
            surfaceLight: '#F3F4F6',
            primary: '#7C3AED',
            primaryHover: '#6D28D9',
            text: '#1F2937',
            textSecondary: '#6B7280',
            border: '#E5E7EB',
            icon: '#6B7280',
            shadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            success: '#059669',
            warning: '#D97706',
            info: '#2563EB',
            error: '#DC2626',
            gradientPrimary: 'linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)',
            gradientSuccess: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
            buttonShadow: '0 4px 12px rgba(5, 150, 105, 0.2)'
        }
    };

    const colors = isDarkMode ? themeColors.dark : themeColors.light;

    // Commission requirements data - updated to include commission rate placeholders
    const commissionRequirements = [
        { level: 1, directReferrals: '1', selfStaking: '-', requirement: '1 Direct Referral', commissionRate: 'N/A' },
        { level: 2, directReferrals: '2', selfStaking: '-', requirement: '2 Direct Referrals', commissionRate: 'N/A' },
        { level: 3, directReferrals: '3', selfStaking: '$200', requirement: '3 Direct Referrals OR Self R-Trade $200', commissionRate: 'N/A' },
        { level: 4, directReferrals: '3', selfStaking: '$200', requirement: '3 Direct Referrals + Self R-Trade $200', commissionRate: 'N/A' },
        { level: 5, directReferrals: '4', selfStaking: '$300', requirement: '4 Direct Referrals + Self R-Trade $300', commissionRate: 'N/A' },
        { level: 6, directReferrals: '5', selfStaking: '$300', requirement: '5 Direct Referrals + Self R-Trade $300', commissionRate: 'N/A' },
        { level: 7, directReferrals: '6', selfStaking: '$400', requirement: '6 Direct Referrals + Self R-Trade $400', commissionRate: 'N/A' },
        { level: 8, directReferrals: '7', selfStaking: '$500', requirement: '7 Direct Referrals + Self R-Trade $500', commissionRate: 'N/A' },
        { level: 9, directReferrals: '8', selfStaking: '$600', requirement: '8 Direct Referrals + Self R-Trade $600', commissionRate: 'N/A' },
        { level: 10, directReferrals: '9', selfStaking: '$700', requirement: '9 Direct Referrals + Self R-Trade $700', commissionRate: 'N/A' }
    ];

    // Transform API record to local format - SIMPLIFIED
    const transformApiRecord = (apiRecord) => {
        return {
            id: apiRecord.commision_id || apiRecord.commission_id || apiRecord.id,
            amount: parseFloat(apiRecord.amount) || 0,
            date: apiRecord.create_date || apiRecord.date,
            type: apiRecord.level === 0 ? 'Self R-Trade Commission' : `Level ${apiRecord.level} Downline Commission`,
            status: apiRecord.status === true ? 'success' : 'disqualified',
            isClaimed: apiRecord.is_claimed || false,
            failedReason: apiRecord.reject_reason || null,
            level: apiRecord.level || 0
        };
    };

    // Get commission type with description
    const getCommissionDescription = (level) => {
        if (level === 0) return 'Commission from your own staking';
        return `Commission from level ${level} downline user's staking`;
    };

    // Get level badge color based on level
    const getLevelBadgeColor = (level) => {
        const levelColors = {
            0: isDarkMode ? '#8B5CF6' : '#7C3AED',
            1: isDarkMode ? '#10B981' : '#059669',
            2: isDarkMode ? '#3B82F6' : '#2563EB',
            3: isDarkMode ? '#F59E0B' : '#D97706',
            4: isDarkMode ? '#EF4444' : '#DC2626',
            5: isDarkMode ? '#8B5CF6' : '#7C3AED',
            6: isDarkMode ? '#10B981' : '#059669',
            7: isDarkMode ? '#3B82F6' : '#2563EB',
            8: isDarkMode ? '#F59E0B' : '#D97706',
            9: isDarkMode ? '#EF4444' : '#DC2626',
            10: isDarkMode ? '#8B5CF6' : '#7C3AED'
        };
        return levelColors[level] || colors.primary;
    };

    // Fetch commission stats
    const fetchCommissionStats = async () => {
        try {
            const response = await axios.post(`${BASE_API_URL}/staking/commission-stats`, {
                token: localStorage.getItem("a5srf4wer7w1cd"),
            });

            const data = response?.data;

            if (data?.error) {
                if (data?.error === "session expired") {
                    localStorage.removeItem("a5srf4wer7w1cd");
                    navigate("/login");
                } else {
                    toast.error(data?.error, {
                        position: 'bottom-center'
                    });
                }
                return;
            }

            if (data?.data) {
                setStats({
                    unclaimed: parseFloat(data.data.unclaimed) || 0,
                    claimed: parseFloat(data.data.claimed) || 0,
                    earned: parseFloat(data.data.earned) || 0,
                    record: parseInt(data.data.record) || 0
                });

                // Update unclaimed amount separately
                setUnclaimedAmount(parseFloat(data.data.unclaimed) || 0);
            }

        } catch (error) {
            toast.error('Failed to load commission stats', {
                position: 'bottom-center'
            });
        }
    };

    // Fetch commission rates from API and merge with requirements
    const fetchCommissionRates = async () => {
        try {
            const response = await axios.post(`${BASE_API_URL}/team/commission-chart`, {
                token: localStorage.getItem("a5srf4wer7w1cd"),
            });

            const data = response?.data;

            if (data?.error) {
                if (data?.error === "session expired") {
                    localStorage.removeItem("a5srf4wer7w1cd");
                    navigate("/login");
                } else if (data?.error !== false) {
                    // Don't show toast for this error since it's not critical
                }
                return;
            }

            if (data?.data) {
                // Filter only staking commissions
                const stakingCommissions = data.data
                    .filter(item => item.type === 'staking')
                    .sort((a, b) => a.level - b.level);

                // Merge commission rates with requirements
                const updatedRequirements = [...commissionRequirements];

                stakingCommissions.forEach(rate => {
                    const levelIndex = updatedRequirements.findIndex(req => req.level === rate.level);
                    if (levelIndex !== -1) {
                        updatedRequirements[levelIndex].commissionRate = `${parseFloat(rate.commission).toFixed(2)}%`;
                    }
                });

                // Also add level 0 (self) if it exists in the API response
                const selfCommission = data.data.find(item => item.level === 0 && item.type === 'staking');
                if (selfCommission) {
                    // Add self level to the beginning if not already in requirements
                    if (!updatedRequirements.find(req => req.level === 0)) {
                        updatedRequirements.unshift({
                            level: 0,
                            directReferrals: '-',
                            selfStaking: 'Any Amount',
                            requirement: 'Self R-Trade',
                            commissionRate: `${parseFloat(selfCommission.commission).toFixed(2)}%`
                        });
                    }
                }

                // Update commission requirements with rates
                setCommissionRates(updatedRequirements);
            }

        } catch (error) {
            // Don't show toast for this error since it's not critical
            // Keep the default commission requirements with 'N/A' rates
            setCommissionRates(commissionRequirements);
        }
    };

    // Fetch commission data from API
    const fetchCommissionData = async (reset = false, filterStatus = statusFilter) => {
        try {
            if (reset) {
                setLoading(true);
                setLastId(0);
            } else {
                setLoadingMore(true);
            }

            const response = await axios.post(`${BASE_API_URL}/staking/commission-list`, {
                token: localStorage.getItem("a5srf4wer7w1cd"),
                last_id: reset ? 0 : lastId,
                status: filterStatus === 'all' ? 'all' : filterStatus
            });

            const data = response?.data;

            if (data?.error) {
                if (data?.error === "session expired") {
                    localStorage.removeItem("a5srf4wer7w1cd");
                    navigate("/login");
                } else {
                    toast.error(data?.error, {
                        position: 'bottom-center'
                    });
                }
                return;
            }

            if (data?.data && Array.isArray(data.data)) {
                const newRecords = data.data.map(transformApiRecord);

                if (reset) {
                    setRecords(newRecords);
                } else {
                    setRecords(prev => [...prev, ...newRecords]);
                }

                // Update pagination state
                if (data.last_id !== undefined) {
                    setLastId(data.last_id);
                }
                setHasMore(data.has_more || false);

                // Update filter counts with total count
                if (reset) {
                    setFilterCounts(prev => ({
                        ...prev,
                        all: data.count || data.data.length
                    }));
                }
            } else {
                if (reset) {
                    setRecords([]);
                }
            }

        } catch (error) {
            toast.error('Failed to load commission data', {
                position: 'bottom-center'
            });
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    // Fetch filter counts (to show all filter tabs even with 0 records)
    const fetchFilterCounts = async () => {
        try {
            // Fetch all records count
            const allResponse = await axios.post(`${BASE_API_URL}/staking/commission-list`, {
                token: localStorage.getItem("a5srf4wer7w1cd"),
                last_id: 0,
                status: 'all',
                limit: 1
            });

            // Fetch success records count
            const successResponse = await axios.post(`${BASE_API_URL}/staking/commission-list`, {
                token: localStorage.getItem("a5srf4wer7w1cd"),
                last_id: 0,
                status: 'success',
                limit: 1
            });

            // Fetch disqualified records count
            const disqualifiedResponse = await axios.post(`${BASE_API_URL}/staking/commission-list`, {
                token: localStorage.getItem("a5srf4wer7w1cd"),
                last_id: 0,
                status: 'disqualified',
                limit: 1
            });

            const allData = allResponse?.data;
            const successData = successResponse?.data;
            const disqualifiedData = disqualifiedResponse?.data;

            // Use count from response if available, otherwise use 0
            const allCount = allData?.count || 0;
            const successCount = successData?.count || 0;
            const disqualifiedCount = disqualifiedData?.count || 0;

            setFilterCounts({
                all: allCount,
                success: successCount,
                disqualified: disqualifiedCount
            });

        } catch (error) {
            // If error, set default counts
            setFilterCounts({
                all: 0,
                success: 0,
                disqualified: 0
            });
        }
    };

    // Claim commission
    const handleClaimCommission = async () => {
        if (unclaimedAmount < MIN_CLAIM_AMOUNT) {
            return;
        }

        try {
            const response = await axios.post(`${BASE_API_URL}/staking/claim`, {
                token: localStorage.getItem("a5srf4wer7w1cd"),
            });

            const data = response?.data;

            if (data?.error) {
                if (data?.error == 'session expired') {
                    localStorage.clear('a5srf4wer7w1cd');
                    navigate('/login');
                } else {
                    toast.error(data?.error, {
                        position: 'bottom-center'
                    });
                }
            }

            if (data?.msg) {
                TriggerBalanceUpdate({ balance: data?.balance, usdt: data?.usdt })
                toast.success(data?.msg, {
                    position: 'bottom-center'
                });

                // Refresh both stats and data
                fetchCommissionStats();
                fetchCommissionData(true, statusFilter);
                fetchFilterCounts();
            }

        } catch (error) {
            toast.error('Failed to claim commission', {
                position: 'bottom-center'
            });
        }
    };

    // Load more records for infinite scroll
    const loadMoreRecords = () => {
        if (!loadingMore && hasMore && lastId > 0) {
            fetchCommissionData(false, statusFilter);
        }
    };

    // Show record details
    const handleShowDetails = (record) => {
        setSelectedRecord(record);
        setShowDetailsModal(true);
    };

    // Handle status filter change
    const handleStatusFilterChange = async (newStatus) => {
        setStatusFilter(newStatus);
        setRecords([]);
        setLastId(0);
        setHasMore(true);
        setLoading(true);

        try {
            const response = await axios.post(`${BASE_API_URL}/staking/commission-list`, {
                token: localStorage.getItem("a5srf4wer7w1cd"),
                last_id: 0,
                status: newStatus === 'all' ? 'all' : newStatus
            });

            const data = response?.data;

            if (data?.error) {
                if (data?.error === "session expired") {
                    localStorage.removeItem("a5srf4wer7w1cd");
                    navigate("/login");
                } else {
                    toast.error(data?.error, {
                        position: 'bottom-center'
                    });
                }
                return;
            }

            if (data?.data && Array.isArray(data.data)) {
                const newRecords = data.data.map(transformApiRecord);
                setRecords(newRecords);

                if (data.last_id !== undefined) {
                    setLastId(data.last_id);
                }
                setHasMore(data.has_more || false);
            }

        } catch (error) {
            toast.error('Failed to load filtered commission data', {
                position: 'bottom-center'
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            await Promise.all([
                fetchCommissionStats(),
                fetchCommissionData(true, 'all'),
                fetchFilterCounts(),
                fetchCommissionRates()
            ]);
        };
        fetchData();
    }, []);

    // Format date to dd/mm/yy format
    const formatShortDate = (dateString) => {
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return 'Invalid date';
            const day = date.getDate().toString().padStart(2, '0');
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            const year = date.getFullYear().toString().slice(-2);
            return `${day}/${month}/${year}`;
        } catch (error) {
            return 'Invalid date';
        }
    };

    // Format date with time for modal
    const formatDateTime = (dateString) => {
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return 'Invalid date';
            const day = date.getDate().toString().padStart(2, '0');
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            const year = date.getFullYear().toString().slice(-2);
            const hours = date.getHours().toString().padStart(2, '0');
            const minutes = date.getMinutes().toString().padStart(2, '0');
            return `${day}/${month}/${year}, ${hours}:${minutes}`;
        } catch (error) {
            return 'Invalid date';
        }
    };

    // Format currency
    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(value);
    };

    // Get status icon
    const getStatusIcon = (status) => {
        switch (status) {
            case 'success':
                return { icon: '✅', color: colors.success, tooltip: 'Success' };
            case 'disqualified':
                return { icon: '❌', color: colors.error, tooltip: 'Disqualified' };
            default:
                return { icon: '⏳', color: colors.warning, tooltip: 'Pending' };
        }
    };

    // Get claimed status icon
    const getClaimedIcon = (isClaimed) => {
        return isClaimed
            ? { icon: '💰', color: colors.success, tooltip: 'Claimed' }
            : { icon: '⏳', color: colors.warning, tooltip: 'Not Claimed' };
    };

    // Animation variants
    const pageVariants = {
        initial: { opacity: 0 },
        animate: {
            opacity: 1,
            transition: {
                duration: 0.4,
                ease: "easeOut"
            }
        }
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.03
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, x: -10 },
        visible: {
            opacity: 1,
            x: 0,
            transition: {
                type: "spring",
                stiffness: 300,
                damping: 24
            }
        }
    };

    const amountCounterVariants = {
        initial: { scale: 0.5, opacity: 0 },
        animate: {
            scale: 1,
            opacity: 1,
            transition: {
                type: "spring",
                stiffness: 200,
                damping: 15
            }
        }
    };

    const pulseAnimation = {
        initial: { scale: 1 },
        animate: {
            scale: [1, 1.02, 1],
            transition: {
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut"
            }
        }
    };

    const claimButtonVariants = {
        initial: { scale: 1 },
        tap: { scale: 0.95 },
        hover: { scale: 1.05 }
    };

    // Custom styles
    const styles = `
        .commission-container {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            user-select: none;
            max-width: 500px;
            margin: 0 auto;
            -webkit-tap-highlight-color: transparent;
        }
        
        /* Loading State */
        .loading-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            min-height: 60vh;
        }
        
        .loading-spinner {
            width: 48px;
            height: 48px;
            border: 3px solid ${colors.primary}40;
            border-top-color: ${colors.primary};
            border-radius: 50%;
            animation: spin 1s linear infinite;
            margin-bottom: 16px;
        }
        
        @keyframes spin {
            to { transform: rotate(360deg); }
        }
        
        /* Empty State */
        .empty-state {
            text-align: center;
            padding: 60px 20px;
            background: ${colors.surface};
            border-radius: 14px;
            border: 1px solid ${colors.border};
            margin-top: 16px;
        }
        
        .empty-icon {
            font-size: 48px;
            margin-bottom: 20px;
            opacity: 0.5;
        }
        
        /* Compact Table Styles */
        .compact-table {
            width: 100%;
            border-collapse: separate;
            border-spacing: 0;
            margin-top: 16px;
            font-size: 12px;
        }
        
        .compact-table th {
            text-align: left;
            padding: 12px 8px;
            font-weight: 600;
            color: ${colors.textSecondary};
            background: ${colors.surfaceLight};
            border-bottom: 1px solid ${colors.border};
            white-space: nowrap;
            font-size: 11px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
        }
        
        .compact-table td {
            padding: 12px 8px;
            color: ${colors.text};
            border-bottom: 1px solid ${colors.border}30;
            vertical-align: middle;
            font-size: 13px;
        }
        
        .compact-table tr:last-child td {
            border-bottom: none;
        }
        
        .compact-table tr:hover td {
            background: ${colors.primary}08;
        }
        
        .status-icon {
            font-size: 16px;
            display: flex;
            align-items: center;
            justify-content: center;
            width: 28px;
            height: 28px;
            border-radius: 50%;
            transition: all 0.2s ease;
            cursor: help;
        }
        
        .status-icon:hover {
            transform: scale(1.1);
        }
        
        .info-button {
            width: 28px;
            height: 28px;
            border-radius: 8px;
            background: ${colors.surfaceLight};
            border: 1px solid ${colors.border};
            color: ${colors.text};
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            font-size: 12px;
            transition: all 0.2s ease;
        }
        
        .info-button:hover {
            background: ${colors.primary};
            color: white;
            border-color: ${colors.primary};
        }
        
        /* Level Badge */
        .level-badge {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            min-width: 22px;
            height: 22px;
            border-radius: 6px;
            font-size: 10px;
            font-weight: 800;
            margin-right: 6px;
            color: white;
        }
        
        /* Type cell styling */
        .type-cell {
            font-size: 12px;
            font-weight: 600;
            display: flex;
            align-items: center;
        }
        
        /* Total Records Badge */
        .total-records-badge {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            padding: 4px 10px;
            border-radius: 12px;
            font-weight: 700;
            font-size: 11px;
            background: ${colors.primary}20;
            color: ${colors.primary};
            border: 1px solid ${colors.primary}40;
        }
        
        /* Filter Tabs */
        .filter-tabs-container {
            margin: 20px 0 16px;
        }
        
        .filter-tabs {
            display: flex;
            gap: 6px;
            margin-bottom: 0;
            flex-wrap: wrap;
        }
        
        .filter-tab {
            padding: 6px 12px;
            border-radius: 10px;
            background: ${colors.surfaceLight};
            border: 1px solid ${colors.border};
            color: ${colors.textSecondary};
            font-size: 12px;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s ease;
            display: flex;
            align-items: center;
            gap: 4px;
        }
        
        .filter-tab.active {
            background: ${colors.primary};
            color: white;
            border-color: ${colors.primary};
        }
        
        .filter-tab:hover:not(.active) {
            background: ${colors.surface};
            border-color: ${colors.primary}50;
        }
        
        .filter-count {
            font-size: 10px;
            background: ${colors.background}80;
            padding: 1px 6px;
            border-radius: 10px;
            min-width: 20px;
            text-align: center;
        }
        
        .filter-tab.active .filter-count {
            background: rgba(255, 255, 255, 0.2);
        }
        
        /* Modal Styles */
        .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.7);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
            padding: 20px;
        }
        
        .modal-content {
            background: ${colors.surface};
            border-radius: 20px;
            max-width: 450px;
            width: 100%;
            max-height: 85vh;
            overflow: hidden;
            border: 1px solid ${colors.border};
            box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
            display: flex;
            flex-direction: column;
        }
        
        .modal-header {
            padding: 24px 24px 16px;
            border-bottom: 1px solid ${colors.border}50;
            position: relative;
        }
        
        .modal-close {
            position: absolute;
            top: 24px;
            right: 24px;
            width: 32px;
            height: 32px;
            border-radius: 10px;
            background: ${colors.surfaceLight};
            border: 1px solid ${colors.border};
            color: ${colors.text};
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            font-size: 18px;
            transition: all 0.2s ease;
        }
        
        .modal-close:hover {
            background: ${colors.primary};
            color: white;
            border-color: ${colors.primary};
        }
        
        .modal-body {
            padding: 24px;
            overflow-y: auto;
            flex: 1;
        }
        
        .modal-section {
            margin-bottom: 20px;
        }
        
        .modal-section-title {
            font-size: 13px;
            font-weight: 700;
            color: ${colors.text};
            margin-bottom: 12px;
            display: flex;
            align-items: center;
        }
        
        .modal-section-icon {
            margin-right: 8px;
            font-size: 14px;
        }
        
        .details-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 12px;
        }
        
        .detail-item {
            padding: 12px;
            background: ${colors.surfaceLight};
            border-radius: 12px;
            border: 1px solid ${colors.border}50;
        }
        
        .detail-label {
            font-size: 10px;
            color: ${colors.textSecondary};
            margin-bottom: 4px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
        }
        
        .detail-value {
            font-size: 13px;
            font-weight: 600;
            color: ${colors.text};
            word-break: break-word;
        }
        
        /* Claim button glow */
        @keyframes glow {
            0%, 100% { box-shadow: 0 0 20px ${colors.primary}40; }
            50% { box-shadow: 0 0 40px ${colors.primary}60; }
        }
        
        /* Loading more indicator */
        .loading-more {
            text-align: center;
            padding: 16px;
            color: ${colors.textSecondary};
            font-size: 12px;
        }
        
        /* Amount cell styling */
        .amount-cell {
            font-weight: 700;
            font-size: 13px;
        }
        
        /* Date cell styling */
        .date-cell {
            color: ${colors.textSecondary};
            font-size: 12px;
            font-family: 'SF Mono', Menlo, Monaco, 'Courier New', monospace;
        }
        
        /* Rules Table */
        .rules-table {
            width: 100%;
            border-collapse: separate;
            border-spacing: 0;
            font-size: 11px;
            margin-top: 8px;
        }
        
        .rules-table th {
            padding: 10px 8px;
            background: ${colors.primary}15;
            color: ${colors.primary};
            font-weight: 700;
            text-align: center;
            border: 1px solid ${colors.border};
            font-size: 10px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
        }
        
        .rules-table td {
            padding: 10px 8px;
            text-align: center;
            border: 1px solid ${colors.border};
            color: ${colors.text};
            font-weight: 600;
            font-size: 10px;
        }
        
        .rules-table tr:nth-child(even) {
            background: ${colors.surfaceLight};
        }
        
        .rules-table tr:hover {
            background: ${colors.primary}08;
        }
        
        .rules-section {
            background: ${colors.surface};
            border-radius: 14px;
            border: 1px solid ${colors.border};
            padding: 16px;
            margin-bottom: 16px;
        }
        
        .rules-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            cursor: pointer;
            margin-bottom: ${showRules ? '16px' : '0'};
        }
        
        .rules-toggle {
            font-size: 20px;
            transition: transform 0.3s ease;
            transform: rotate(${showRules ? '180deg' : '0deg'});
        }
        
        /* Info Cards */
        .info-card {
            background: ${colors.surfaceLight};
            border-radius: 12px;
            padding: 12px;
            border: 1px solid ${colors.border};
            margin-bottom: 12px;
        }
        
        .info-card-header {
            display: flex;
            align-items: center;
            gap: 8px;
            margin-bottom: 8px;
        }
        
        .info-card-icon {
            font-size: 16px;
            width: 28px;
            height: 28px;
            border-radius: 8px;
            background: ${colors.primary}20;
            color: ${colors.primary};
            display: flex;
            align-items: center;
            justifyContent: center;
        }
        
        /* Data Container */
        .data-container {
            margin-top: 16px;
        }
        
        /* Filter tabs always visible */
        .filter-tabs-wrapper {
            position: sticky;
            top: 0;
            z-index: 100;
            background: ${colors.background};
            padding: 8px 0;
            margin: -8px 0 8px;
        }
        
        /* Responsive */
        @media (max-width: 768px) {
            .commission-container {
                padding: 16px !important;
            }
            
            .compact-table {
                font-size: 11px;
            }
            
            .compact-table th,
            .compact-table td {
                padding: 10px 6px;
            }
            
            .type-cell {
                font-size: 11px;
            }
            
            .level-badge {
                min-width: 20px;
                height: 20px;
                font-size: 9px;
                margin-right: 4px;
            }
            
            .filter-tabs {
                overflow-x: auto;
                flex-wrap: nowrap;
                padding-bottom: 8px;
            }
            
            .modal-content {
                max-height: 90vh;
            }
            
            .modal-body {
                padding: 20px;
            }
            
            .details-grid {
                grid-template-columns: 1fr;
                gap: 10px;
            }
            
            .amount-cell {
                font-size: 12px;
            }
            
            .date-cell {
                font-size: 11px;
            }
            
            .rules-table {
                font-size: 10px;
            }
            
            .rules-table th,
            .rules-table td {
                padding: 8px 4px;
                font-size: 9px;
            }
            
            .filter-tab {
                font-size: 11px;
                padding: 5px 10px;
            }
            
            .filter-count {
                font-size: 9px;
                min-width: 18px;
            }
        }
        
        @media (max-width: 380px) {
            .compact-table {
                font-size: 10px;
            }
            
            .compact-table th,
            .compact-table td {
                padding: 8px 4px;
            }
            
            .type-cell {
                font-size: 10px;
            }
            
            .level-badge {
                min-width: 18px;
                height: 18px;
                font-size: 8px;
                margin-right: 3px;
            }
            
            .status-icon {
                font-size: 14px;
                width: 24px;
                height: 24px;
            }
            
            .info-button {
                width: 24px;
                height: 24px;
                font-size: 10px;
            }
            
            .total-records-badge {
                font-size: 10px;
                padding: 3px 8px;
            }
            
            .filter-tab {
                font-size: 10px;
                padding: 4px 8px;
            }
            
            .filter-count {
                font-size: 8px;
                min-width: 16px;
                padding: 1px 4px;
            }
        }
    `;

    return (
        <motion.div
            variants={pageVariants}
            initial="initial"
            animate="animate"
            className="commission-container"
            style={{
                minHeight: '100vh',
                padding: '20px 16px',
                background: colors.background,
                color: colors.text,
                transition: 'background 0.25s ease, color 0.25s ease'
            }}
        >
            <style>{styles}</style>

            <div className="container-fluid px-0">
                {/* Header */}
                <div className="d-flex align-items-center justify-content-between mb-6">
                    <div>
                        <h1 className="mb-1" style={{
                            fontWeight: 800,
                            fontSize: '24px',
                            color: colors.text
                        }}>
                            My Commission
                        </h1>
                        <p className="mb-0" style={{
                            color: colors.textSecondary,
                            fontSize: '13px',
                            opacity: 0.8
                        }}>
                            Track and claim your earnings
                        </p>
                    </div>
                    <motion.div
                        initial={{ rotate: -20 }}
                        animate={{ rotate: 0 }}
                        transition={{ type: "spring", stiffness: 200 }}
                        style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '10px',
                            background: colors.gradientPrimary,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#FFFFFF',
                            fontWeight: 700,
                            fontSize: '16px'
                        }}
                    >
                        💰
                    </motion.div>
                </div>

                {/* Commission Rules Section */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="rules-section"
                >
                    <div className="rules-header" onClick={() => setShowRules(!showRules)}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{
                                width: '32px',
                                height: '32px',
                                borderRadius: '8px',
                                background: colors.gradientPrimary,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontSize: '14px'
                            }}>
                                📊
                            </div>
                            <div>
                                <div style={{
                                    fontSize: '14px',
                                    fontWeight: 700,
                                    color: colors.text
                                }}>
                                    Commission Requirements & Rates
                                </div>
                                <div style={{
                                    fontSize: '11px',
                                    color: colors.textSecondary
                                }}>
                                    Click to {showRules ? 'hide' : 'show'} details
                                </div>
                            </div>
                        </div>
                        <div className="rules-toggle">▼</div>
                    </div>

                    <AnimatePresence>
                        {showRules && (
                            <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.3 }}
                                style={{ overflow: 'hidden' }}
                            >
                                <div style={{
                                    fontSize: '11px',
                                    color: colors.textSecondary,
                                    marginBottom: '12px',
                                    lineHeight: 1.4,
                                    padding: '8px 0'
                                }}>
                                    Meet these requirements to qualify for downline commission at each level
                                </div>

                                <div style={{ overflowX: 'auto' }}>
                                    <table className="rules-table">
                                        <thead>
                                            <tr>
                                                <th style={{ width: '12%' }}>Level</th>
                                                <th style={{ width: '18%' }}>Direct Referrals</th>
                                                <th style={{ width: '18%' }}>Self R-Trade</th>
                                                <th style={{ width: '18%' }}>Commission Rate</th>
                                                <th style={{ width: '34%' }}>Requirement</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(commissionRates.length > 0 ? commissionRates : commissionRequirements).map((rule, index) => (
                                                <tr key={rule.level}>
                                                    <td style={{
                                                        background: getLevelBadgeColor(rule.level),
                                                        color: 'white',
                                                        fontWeight: 800,
                                                        fontSize: '10px'
                                                    }}>
                                                        {rule.level === 0 ? 'Self' : `L${rule.level}`}
                                                    </td>
                                                    <td style={{
                                                        color: rule.directReferrals === '-' ? colors.textSecondary : colors.text,
                                                        fontWeight: rule.directReferrals === '-' ? 400 : 700,
                                                        fontSize: '10px'
                                                    }}>
                                                        {rule.directReferrals}
                                                    </td>
                                                    <td style={{
                                                        color: rule.selfStaking === '-' ? colors.textSecondary : colors.success,
                                                        fontWeight: rule.selfStaking === '-' ? 400 : 700,
                                                        fontSize: '10px'
                                                    }}>
                                                        {rule.selfStaking}
                                                    </td>
                                                    <td style={{
                                                        color: rule.commissionRate === 'N/A' ? colors.textSecondary : colors.primary,
                                                        fontWeight: 800,
                                                        fontSize: '10px',
                                                        background: rule.commissionRate === 'N/A' ? 'transparent' : `${colors.primary}15`,
                                                        border: rule.commissionRate === 'N/A' ? 'none' : `1px solid ${colors.primary}30`,
                                                        borderRadius: '4px',
                                                        padding: '4px 2px'
                                                    }}>
                                                        {rule.commissionRate}
                                                    </td>
                                                    <td style={{
                                                        fontSize: '9px',
                                                        textAlign: 'left',
                                                        padding: '8px',
                                                        color: colors.text
                                                    }}>
                                                        {rule.requirement}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Important Notes */}
                                <div style={{
                                    marginTop: '16px',
                                    padding: '12px',
                                    background: colors.warning + '10',
                                    borderRadius: '10px',
                                    border: `1px solid ${colors.warning}30`
                                }}>
                                    <div style={{
                                        fontSize: '10px',
                                        fontWeight: 700,
                                        color: colors.warning,
                                        marginBottom: '6px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px'
                                    }}>
                                        ⚡ IMPORTANT NOTES
                                    </div>
                                    <ul style={{
                                        margin: 0,
                                        paddingLeft: '16px',
                                        fontSize: '9px',
                                        color: colors.textSecondary,
                                        lineHeight: 1.4
                                    }}>
                                        <li>Level 1-2: Only direct referrals required</li>
                                        <li>Level 3: Either 3 direct referrals OR $200 self R-Trade</li>
                                        <li>Level 4+: Both direct referrals AND self R-Trade required</li>
                                        <li>All requirements must be maintained to continue earning</li>
                                        <li>Commission rates are percentages of downline's R-Trade amount</li>
                                    </ul>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </motion.div>

                {/* Unclaimed Commission Section */}
                <motion.div
                    variants={amountCounterVariants}
                    initial="initial"
                    animate="animate"
                    className="mb-6"
                    style={{
                        background: `linear-gradient(135deg, ${colors.success}15 0%, ${colors.surface} 100%)`,
                        borderRadius: '18px',
                        border: `2px solid ${colors.success}40`,
                        padding: '24px 20px',
                        margin: '20px 0',
                        boxShadow: `0 8px 32px ${isDarkMode ? 'rgba(16, 185, 129, 0.15)' : 'rgba(5, 150, 105, 0.1)'},
                                    inset 0 1px 0 ${colors.success}20`,
                        position: 'relative',
                        overflow: 'hidden'
                    }}
                >
                    {/* Animated background effect */}
                    <motion.div
                        animate={{
                            scale: [1, 1.2, 1],
                            opacity: [0.1, 0.05, 0.1]
                        }}
                        transition={{
                            duration: 4,
                            repeat: Infinity,
                            ease: "linear"
                        }}
                        style={{
                            position: 'absolute',
                            top: '-50%',
                            right: '-50%',
                            width: '200%',
                            height: '200%',
                            background: `radial-gradient(circle, ${colors.success} 0%, transparent 70%)`,
                            zIndex: 0
                        }}
                    />

                    <div style={{ position: 'relative', zIndex: 1 }}>
                        <div className="text-center mb-4">
                            <div style={{
                                fontSize: '11px',
                                color: colors.success,
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '1px',
                                marginBottom: '8px'
                            }}>
                                ⚡ AVAILABLE TO CLAIM
                            </div>
                            <motion.h2
                                variants={pulseAnimation}
                                initial="initial"
                                animate="animate"
                                style={{
                                    fontSize: '36px',
                                    fontWeight: 900,
                                    color: colors.success,
                                    margin: '0 0 12px 0',
                                    textShadow: `0 2px 10px ${colors.success}40`,
                                    textAlign: 'center'
                                }}
                            >
                                ${formatCurrency(stats.unclaimed)}
                            </motion.h2>
                            <p style={{
                                fontSize: '13px',
                                color: colors.textSecondary,
                                marginBottom: '12px',
                                lineHeight: 1.5,
                                textAlign: 'center'
                            }}>
                                Total commission waiting to be claimed
                            </p>

                            {/* Minimum amount warning */}
                            {stats.unclaimed > 0 && stats.unclaimed < MIN_CLAIM_AMOUNT && (
                                <motion.div
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    style={{
                                        padding: '8px 12px',
                                        background: colors.warning + '20',
                                        border: `1px solid ${colors.warning}40`,
                                        borderRadius: '10px',
                                        marginBottom: '12px'
                                    }}
                                >
                                    <div style={{
                                        fontSize: '11px',
                                        fontWeight: 600,
                                        color: colors.warning,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '6px',
                                        textAlign: 'center'
                                    }}>
                                        <span>ℹ️</span>
                                        Minimum claim amount: ${MIN_CLAIM_AMOUNT}
                                    </div>
                                    <div style={{
                                        fontSize: '10px',
                                        color: colors.textSecondary,
                                        marginTop: '4px',
                                        textAlign: 'center'
                                    }}>
                                        You need ${(MIN_CLAIM_AMOUNT - stats.unclaimed).toFixed(2)} more to claim
                                    </div>
                                </motion.div>
                            )}
                        </div>

                        <motion.div
                            variants={claimButtonVariants}
                            whileHover={stats.unclaimed >= MIN_CLAIM_AMOUNT ? "hover" : undefined}
                            whileTap={stats.unclaimed >= MIN_CLAIM_AMOUNT ? "tap" : undefined}
                            style={{
                                width: '100%',
                                padding: '14px',
                                background: stats.unclaimed >= MIN_CLAIM_AMOUNT ? colors.gradientSuccess : colors.border,
                                color: stats.unclaimed >= MIN_CLAIM_AMOUNT ? '#0F172A' : '#FFFFFF',
                                borderRadius: '12px',
                                fontWeight: 800,
                                fontSize: '14px',
                                cursor: stats.unclaimed >= MIN_CLAIM_AMOUNT ? 'pointer' : 'not-allowed',
                                letterSpacing: '0.5px',
                                opacity: stats.unclaimed >= MIN_CLAIM_AMOUNT ? 1 : 0.6,
                                animation: stats.unclaimed >= MIN_CLAIM_AMOUNT ? 'glow 2s infinite' : 'none',
                                textAlign: 'center',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px',
                                boxShadow: stats.unclaimed >= MIN_CLAIM_AMOUNT ? colors.buttonShadow : 'none',
                                border: stats.unclaimed >= MIN_CLAIM_AMOUNT
                                    ? `2px solid ${colors.success}`
                                    : `2px solid ${colors.border}`
                            }}
                            onClick={handleClaimCommission}
                        >
                            <span style={{
                                fontSize: '16px',
                                fontWeight: 700,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '100%'
                            }}>
                                CLAIM ${formatCurrency(stats.unclaimed)}
                            </span>
                        </motion.div>
                    </div>
                </motion.div>

                {/* Stats Summary */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="mb-6"
                    style={{
                        background: colors.surface,
                        borderRadius: '14px',
                        border: `1px solid ${colors.border}`,
                        padding: '16px'
                    }}
                >
                    <div className="row">
                        <div className="col-4 text-center">
                            <div style={{
                                fontSize: '20px',
                                fontWeight: 800,
                                color: colors.success,
                                marginBottom: '4px'
                            }}>
                                ${formatCurrency(stats.unclaimed)}
                            </div>
                            <div style={{
                                fontSize: '11px',
                                color: colors.textSecondary,
                                fontWeight: 600
                            }}>
                                Unclaimed
                            </div>
                        </div>
                        <div className="col-4 text-center">
                            <div style={{
                                fontSize: '20px',
                                fontWeight: 800,
                                color: colors.primary,
                                marginBottom: '4px'
                            }}>
                                ${formatCurrency(stats.earned)}
                            </div>
                            <div style={{
                                fontSize: '11px',
                                color: colors.textSecondary,
                                fontWeight: 600
                            }}>
                                Total Earned
                            </div>
                        </div>
                        <div className="col-4 text-center">
                            <div style={{
                                fontSize: '20px',
                                fontWeight: 800,
                                color: colors.info,
                                marginBottom: '4px'
                            }}>
                                ${formatCurrency(stats.claimed)}
                            </div>
                            <div style={{
                                fontSize: '11px',
                                color: colors.textSecondary,
                                fontWeight: 600
                            }}>
                                Claimed
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* Commission Records Section - Always Visible Header */}
                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                    className="data-container"
                >
                    <div className="d-flex align-items-center justify-content-between mb-4">
                        <h2 style={{
                            fontSize: '18px',
                            fontWeight: 700,
                            color: colors.text,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            justifyContent: 'space-between'
                        }}>
                            <span>
                                Commission Records
                            </span>
                            <span className="total-records-badge">
                                📊 Total: {stats.record}
                            </span>
                        </h2>
                    </div>

                    {/* Status Filter Tabs - ALWAYS VISIBLE regardless of data */}
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="filter-tabs-container"
                    >
                        <div className="filter-tabs">
                            {[
                                { id: 'all', label: 'All Records', count: filterCounts.all },
                                { id: 'success', label: 'Success', count: filterCounts.success },
                                { id: 'disqualified', label: 'Disqualified', count: filterCounts.disqualified }
                            ].map(filter => (
                                <motion.button
                                    key={filter.id}
                                    whileTap={{ scale: 0.95 }}
                                    className={`filter-tab ${statusFilter === filter.id ? 'active' : ''}`}
                                    onClick={() => handleStatusFilterChange(filter.id)}
                                    disabled={loading}
                                    style={{
                                        opacity: loading ? 0.6 : 1,
                                        cursor: loading ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    {filter.label}
                                    <span className="filter-count">
                                        {filter.count}
                                    </span>
                                </motion.button>
                            ))}
                        </div>
                    </motion.div>

                    {/* Data Display Area */}
                    {loading ? (
                        <div className="loading-container" style={{ minHeight: '200px' }}>
                            <div className="loading-spinner"></div>
                            <div style={{ color: colors.textSecondary, fontSize: '13px' }}>
                                Loading commission data...
                            </div>
                        </div>
                    ) : records.length === 0 ? (
                        /* Empty State - Show when no data for current filter */
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="empty-state"
                        >
                            <div className="empty-icon">📊</div>
                            <h3 style={{
                                fontSize: '18px',
                                fontWeight: 700,
                                color: colors.text,
                                marginBottom: '10px'
                            }}>
                                No Commission Records
                            </h3>
                            <p style={{
                                fontSize: '13px',
                                color: colors.textSecondary,
                                marginBottom: '20px',
                                lineHeight: 1.5
                            }}>
                                {statusFilter === 'all'
                                    ? 'Start earning commissions to see records here'
                                    : `No ${statusFilter} commission records found`
                                }
                            </p>
                            <div style={{
                                fontSize: '11px',
                                color: colors.textSecondary,
                                padding: '12px',
                                background: colors.surfaceLight,
                                borderRadius: '10px',
                                border: `1px solid ${colors.border}`
                            }}>
                                💡 <strong>Tip:</strong> Try switching to a different filter tab above
                            </div>
                        </motion.div>
                    ) : (
                        /* Commission Records Table */
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            style={{
                                overflowX: 'auto',
                                borderRadius: '14px',
                                border: `1px solid ${colors.border}`,
                                background: colors.surface
                            }}
                        >
                            <table className="compact-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: '20%' }}>Date</th>
                                        <th style={{ width: '25%' }}>Level</th>
                                        <th style={{ width: '25%' }}>Amount</th>
                                        <th style={{ width: '15%', textAlign: 'center' }}>Status</th>
                                        <th style={{ width: '15%', textAlign: 'center' }}>Details</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {records.map((record, index) => {
                                        const statusIcon = getStatusIcon(record.status);
                                        const isLastRecord = index === records.length - 1;
                                        const levelColor = getLevelBadgeColor(record.level);

                                        return (
                                            <tr
                                                key={`${record.id}-${index}`}
                                                variants={itemVariants}
                                                ref={isLastRecord ? lastRecordRef : null}
                                            >
                                                <td className="date-cell">
                                                    {formatShortDate(record.date)}
                                                </td>
                                                <td className="type-cell">
                                                    <span
                                                        className="level-badge"
                                                        style={{ background: levelColor }}
                                                    >
                                                        {record.level === 0 ? 'Self' : `L${record.level}`}
                                                    </span>
                                                </td>
                                                <td className="amount-cell">
                                                    ${formatCurrency(record.amount)}
                                                </td>
                                                <td style={{ textAlign: 'center' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        <motion.div
                                                            className="status-icon"
                                                            title={statusIcon.tooltip}
                                                            whileHover={{ scale: 1.1 }}
                                                            whileTap={{ scale: 0.9 }}
                                                            style={{
                                                                color: statusIcon.color,
                                                                background: `${statusIcon.color}15`
                                                            }}
                                                        >
                                                            {statusIcon.icon}
                                                        </motion.div>
                                                    </div>
                                                </td>
                                                <td style={{ textAlign: 'center' }}>
                                                    <motion.button
                                                        whileTap={{ scale: 0.9 }}
                                                        className="info-button"
                                                        onClick={() => handleShowDetails(record)}
                                                        title="View Details"
                                                    >
                                                        ℹ️
                                                    </motion.button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>

                            {/* Loading More Indicator */}
                            {loadingMore && (
                                <div className="loading-more">
                                    <div style={{
                                        width: '20px',
                                        height: '20px',
                                        border: `2px solid ${colors.primary}40`,
                                        borderTopColor: colors.primary,
                                        borderRadius: '50%',
                                        animation: 'spin 1s linear infinite',
                                        margin: '0 auto 8px'
                                    }} />
                                    Loading more records...
                                </div>
                            )}

                            {/* No More Records */}
                            {!hasMore && records.length > 0 && (
                                <div className="loading-more" style={{ color: colors.textSecondary }}>
                                    No more records to load
                                </div>
                            )}
                        </motion.div>
                    )}
                </motion.div>
            </div>

            {/* Commission Details Modal */}
            <AnimatePresence>
                {showDetailsModal && selectedRecord && (
                    <motion.div
                        className="modal-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setShowDetailsModal(false)}
                    >
                        <motion.div
                            className="modal-content"
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.8, opacity: 0 }}
                            transition={{ type: "spring", stiffness: 300, damping: 25 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="modal-header">
                                <h2 style={{
                                    fontSize: '18px',
                                    fontWeight: 800,
                                    color: colors.text,
                                    margin: 0
                                }}>
                                    Commission Details
                                </h2>
                                <button
                                    className="modal-close"
                                    onClick={() => setShowDetailsModal(false)}
                                >
                                    ×
                                </button>
                            </div>

                            <div className="modal-body">
                                {/* Basic Information */}
                                <div className="modal-section">
                                    <div className="modal-section-title">
                                        <span className="modal-section-icon">📋</span>
                                        Basic Information
                                    </div>
                                    <div className="details-grid">
                                        <div className="detail-item">
                                            <div className="detail-label">Amount</div>
                                            <div className="detail-value" style={{
                                                fontSize: '16px',
                                                fontWeight: 800,
                                                color: selectedRecord.status === 'success' ? colors.success : colors.error
                                            }}>
                                                ${formatCurrency(selectedRecord.amount)}
                                            </div>
                                        </div>
                                        <div className="detail-item">
                                            <div className="detail-label">Commission Type</div>
                                            <div className="detail-value">
                                                <span style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '6px'
                                                }}>
                                                    <span style={{
                                                        color: selectedRecord.level === 0 ? colors.primary : colors.info,
                                                        fontSize: '14px'
                                                    }}>
                                                        {selectedRecord.level === 0 ? '👤' : '👥'}
                                                    </span>
                                                    <span>
                                                        {selectedRecord.level === 0 ? 'Self R-Trade' : `Level ${selectedRecord.level} Downline`}
                                                    </span>
                                                </span>
                                            </div>
                                        </div>
                                        <div className="detail-item">
                                            <div className="detail-label">Date</div>
                                            <div className="detail-value">
                                                {formatDateTime(selectedRecord.date)}
                                            </div>
                                        </div>
                                        <div className="detail-item">
                                            <div className="detail-label">Status</div>
                                            <div className="detail-value">
                                                <span style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '6px'
                                                }}>
                                                    <span style={{
                                                        color: getStatusIcon(selectedRecord.status).color,
                                                        fontSize: '14px'
                                                    }}>
                                                        {getStatusIcon(selectedRecord.status).icon}
                                                    </span>
                                                    <span style={{ textTransform: 'capitalize' }}>
                                                        {selectedRecord.status}
                                                    </span>
                                                </span>
                                            </div>
                                        </div>
                                        {selectedRecord.status === 'success' && (
                                            <div className="detail-item">
                                                <div className="detail-label">Claimed Status</div>
                                                <div className="detail-value">
                                                    <span style={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: '6px'
                                                    }}>
                                                        <span style={{
                                                            color: getClaimedIcon(selectedRecord.isClaimed).color,
                                                            fontSize: '12px'
                                                        }}>
                                                            {getClaimedIcon(selectedRecord.isClaimed).icon}
                                                        </span>
                                                        <span>
                                                            {selectedRecord.isClaimed ? 'Claimed' : 'Not Claimed'}
                                                        </span>
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Failed Reason (if disqualified) */}
                                {selectedRecord.status === 'disqualified' && selectedRecord.failedReason && (
                                    <div className="modal-section">
                                        <div className="modal-section-title">
                                            <span className="modal-section-icon">⚠️</span>
                                            Disqualify Reason
                                        </div>
                                        <div style={{
                                            padding: '14px',
                                            background: colors.error + '10',
                                            borderRadius: '12px',
                                            border: `1px solid ${colors.error}30`
                                        }}>
                                            <div style={{
                                                fontSize: '13px',
                                                color: colors.text,
                                                lineHeight: 1.5,
                                                marginBottom: '8px',
                                                fontWeight: 600
                                            }}>
                                                Why this commission was disqualified:
                                            </div>
                                            <div style={{
                                                fontSize: '12px',
                                                color: colors.textSecondary,
                                                lineHeight: 1.4,
                                                padding: '8px',
                                                background: colors.surface,
                                                borderRadius: '8px',
                                                border: `1px solid ${colors.border}`
                                            }}>
                                                {selectedRecord.failedReason}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Success Info (if successful) */}
                                {selectedRecord.status === 'success' && (
                                    <div className="modal-section">
                                        <div className="modal-section-title">
                                            <span className="modal-section-icon">💡</span>
                                            Commission Information
                                        </div>
                                        <div style={{
                                            padding: '14px',
                                            background: selectedRecord.level === 0
                                                ? colors.primary + '10'
                                                : colors.success + '10',
                                            borderRadius: '12px',
                                            border: `1px solid ${selectedRecord.level === 0 ? colors.primary + '30' : colors.success + '30'}`
                                        }}>
                                            <div style={{
                                                fontSize: '12px',
                                                color: selectedRecord.level === 0 ? colors.primary : colors.success,
                                                lineHeight: 1.5,
                                                marginBottom: '6px',
                                                fontWeight: 600,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px'
                                            }}>
                                                {selectedRecord.level === 0 ? '💰 Self R-Trade' : '👥 Downline'}
                                                <span style={{
                                                    padding: '2px 6px',
                                                    borderRadius: '6px',
                                                    background: selectedRecord.level === 0
                                                        ? colors.primary + '20'
                                                        : colors.success + '20',
                                                    fontSize: '10px',
                                                    fontWeight: 700
                                                }}>
                                                    {selectedRecord.level === 0 ? 'Self' : `Level ${selectedRecord.level}`}
                                                </span>
                                            </div>
                                            <div style={{
                                                fontSize: '11px',
                                                color: colors.textSecondary,
                                                lineHeight: 1.4
                                            }}>
                                                {selectedRecord.level === 0
                                                    ? 'This commission is earned from your own staking activity.'
                                                    : `This commission is earned from your level ${selectedRecord.level} downline user's staking activity.`
                                                }
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

export default StakingCommission;